// Money formatting helpers. Inputs are always integer MINOR units; we only
// divide for DISPLAY. No bet/balance/payout arithmetic happens here — the server
// is authoritative and all real math stays in integer minor units.
//
// Minor units are NOT universally 2 decimals: JPY has 0, most currencies 2, and
// a few (e.g. BHD, KWD) have 3. We resolve the correct exponent per currency
// from the platform's Intl data so a value like 100000000 minor renders as
// ¥100,000,000 (JPY, exp 0) rather than ¥1,000,000.00.
const exponentCache = new Map();
/**
 * Minor-unit exponent for a currency (decimal places). Resolved from Intl's
 * currency data (the single source of truth), cached per code, defaulting to 2
 * for unknown/malformed codes. Pure lookup — never used for money arithmetic.
 */
export function minorExponent(currency) {
    const code = (currency || 'USD').toUpperCase();
    const cached = exponentCache.get(code);
    if (cached !== undefined)
        return cached;
    let exp = 2;
    try {
        const opts = new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).resolvedOptions();
        if (typeof opts.maximumFractionDigits === 'number')
            exp = opts.maximumFractionDigits;
    }
    catch {
        exp = 2;
    }
    exponentCache.set(code, exp);
    return exp;
}
/** Formats integer minor units as a localized currency string for display. */
export function formatMoney(minor, currency) {
    const exp = minorExponent(currency);
    const major = minor / 10 ** exp;
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currency || 'USD',
        }).format(major);
    }
    catch {
        // Unknown/malformed ISO code: a safe generic fallback that still respects
        // the resolved exponent and never throws.
        return `${major.toFixed(exp)} ${currency}`;
    }
}
/** The showcase demo bet ladder in MINOR units (server stays authoritative). */
export const DEMO_BET_DENOMINATIONS = [20, 40, 100, 200, 500, 1000, 2000];
/** Canonical default selected bet ($1.00). */
export const DEFAULT_BET_MINOR = 100;
/**
 * Picks the nearest valid denomination to `desired` from `ladder`, preferring
 * the canonical default when the ladder is empty. Pure integer comparison.
 */
export function clampBetToLadder(desired, ladder) {
    if (ladder.length === 0)
        return DEFAULT_BET_MINOR;
    if (ladder.includes(desired))
        return desired;
    let best = ladder[0];
    let bestDist = Math.abs(desired - best);
    for (const v of ladder) {
        const d = Math.abs(desired - v);
        if (d < bestDist) {
            best = v;
            bestDist = d;
        }
    }
    return best;
}
//# sourceMappingURL=money.js.map